import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';

export interface EventqFileDropzonePreview {
  readonly file: File;
  readonly url: string;
  readonly label: string;
}

@Component({
  selector: 'eventq-file-dropzone',
  imports: [MatIcon],
  templateUrl: './file-dropzone.html',
  styleUrl: './file-dropzone.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.eventq-file-dropzone-host--dragging]': 'dragging()',
    '[class.eventq-file-dropzone-host--disabled]': 'disabled()',
    '[class.eventq-file-dropzone-host--has-files]': 'previews().length > 0',
    '[attr.disabled]': 'disabled() ? "" : null',
  },
})
export class EventqFileDropzone {
  private readonly destroyRef = inject(DestroyRef);

  readonly label = input('Drag and Drop files here');
  readonly hint = input<string | null>(null);
  readonly disabled = input(false);
  readonly accept = input<string | null>(null);
  readonly multiple = input(true);
  /** Controlled file list (e.g. from action-form values). */
  readonly files = input<readonly File[]>([]);
  /** Optional consumer hook for upload / side effects. */
  readonly onFilesSelected = input<((files: File[]) => void) | null>(null);

  readonly filesChange = output<File[]>();

  readonly dragging = signal(false);
  readonly previews = signal<readonly EventqFileDropzonePreview[]>([]);

  constructor() {
    effect(() => {
      const nextFiles = this.files();
      untracked(() => {
        for (const item of this.previews()) {
          URL.revokeObjectURL(item.url);
        }
      });
      this.previews.set(
        nextFiles.map((file) => ({
          file,
          url: URL.createObjectURL(file),
          label: file.name,
        })),
      );
    });

    this.destroyRef.onDestroy(() => {
      for (const item of this.previews()) {
        URL.revokeObjectURL(item.url);
      }
    });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (this.disabled()) {
      return;
    }
    this.dragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(false);
    if (this.disabled()) {
      return;
    }
    const files = Array.from(event.dataTransfer?.files ?? []);
    this.appendFiles(files);
  }

  onFileInputChange(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    const files = Array.from(inputEl.files ?? []);
    this.appendFiles(files);
    inputEl.value = '';
  }

  removeAt(index: number, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled()) {
      return;
    }
    const next = this.files().filter((_, i) => i !== index);
    this.emitFiles(next);
  }

  private appendFiles(incoming: File[]): void {
    if (!incoming.length) {
      return;
    }
    const next = this.multiple() ? [...this.files(), ...incoming] : [incoming[0]!];
    this.emitFiles(next);
  }

  private emitFiles(files: File[]): void {
    this.filesChange.emit(files);
    this.onFilesSelected()?.(files);
  }
}
